// src/pages/About.jsx - Modified to use server-side OG generation
// Social media scrapers get OG tags from API, not from client-side Helmet

import React, { useState, useRef, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FiArrowRight,
  FiMail,
  FiGithub,
  FiLinkedin,
  FiDribbble,
  FiInstagram,
  FiCalendar,
  FiSmile,
  FiCoffee,
  FiZap,
  FiEye,
  FiBookOpen,
  FiExternalLink,
  FiPenTool,
  FiPackage,
  FiGrid,
  FiMessageCircle,
  FiTrendingUp,
  FiStar,
  FiClock,
  FiTarget,
  FiCompass,
  FiLayers,
  FiUser,
  FiHeart,
  FiCheck,
  FiX,
  FiBriefcase,
  FiGlobe,
} from 'react-icons/fi';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import StatsSection from '../components/StatsSection';
import { FiverrCard } from '../components/FiverrCard';
import ReviewsSection from '../components/ReviewsSection';
// ✅ REMOVED: ogImageService - No client-side OG generation
// import { ogImageService } from '../services/ogImageService';

// =============================================
// GLASSY DIVIDER
// =============================================
const GlassyDivider = () => {
  return (
    <div className="relative py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="relative h-px w-full overflow-hidden">
          <motion.div
            animate={{
              x: ['-100%', '100%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute inset-0 w-1/3"
            style={{
              background:
                'linear-gradient(to right, transparent, var(--text), transparent)',
              opacity: 0.06,
            }}
          />
        </div>
      </div>
    </div>
  );
};

// =============================================
// GLASSY CARD - Black & White
// =============================================
const GlassyCard = ({ children, className = '' }) => {
  return (
    <div
      className={`relative overflow-hidden backdrop-blur-xl ${className}`}
      style={{
        backgroundColor: 'rgba(var(--bg-rgb), 0.3)',
        border: '1px solid rgba(var(--text-rgb), 0.06)',
        borderRadius: '16px',
        boxShadow: 'none',
      }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-white/3 via-transparent to-transparent" />
        <div className="absolute -inset-full w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -translate-x-full animate-[shimmer_8s_infinite]" />
      </div>
      {children}
    </div>
  );
};

// =============================================
// LIQUID GRADIENT HERO
// =============================================
const AboutHero = () => {
  return (
    <div className="relative mb-8 overflow-hidden rounded-3xl">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 dark:from-blue-500/10 dark:via-transparent dark:to-purple-500/10" />

      <motion.div
        animate={{
          x: ['-20%', '15%', '-5%', '20%', '-20%'],
          y: ['-15%', '10%', '-10%', '15%', '-15%'],
          scale: [1, 1.3, 0.9, 1.2, 1],
          borderRadius: [
            '60% 40% 30% 70%',
            '40% 60% 70% 30%',
            '30% 70% 60% 40%',
            '70% 30% 40% 60%',
            '60% 40% 30% 70%',
          ],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute top-5 left-5 w-72 h-72 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #3b82f6 0%, #60a5fa 40%, transparent 70%)',
          opacity: 0.1,
        }}
      />

      <motion.div
        animate={{
          x: ['15%', '-15%', '10%', '-10%', '15%'],
          y: ['15%', '-10%', '20%', '-15%', '15%'],
          scale: [1.2, 0.9, 1.4, 0.8, 1.2],
          borderRadius: [
            '40% 60% 70% 30%',
            '60% 40% 30% 70%',
            '70% 30% 40% 60%',
            '30% 70% 60% 40%',
            '40% 60% 70% 30%',
          ],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute bottom-5 right-5 w-80 h-80 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #8b5cf6 0%, #a78bfa 40%, transparent 70%)',
          opacity: 0.08,
        }}
      />

      <motion.div
        animate={{
          x: ['-5%', '10%', '-10%', '5%', '-5%'],
          y: ['10%', '-15%', '5%', '-10%', '10%'],
          scale: [1, 1.2, 0.8, 1.3, 1],
          borderRadius: [
            '50% 30% 60% 40%',
            '30% 60% 40% 70%',
            '60% 40% 70% 30%',
            '40% 70% 30% 60%',
            '50% 30% 60% 40%',
          ],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #06b6d4 0%, #22d3ee 40%, transparent 70%)',
          opacity: 0.06,
        }}
      />

      <motion.div
        animate={{
          x: ['10%', '-20%', '15%', '-5%', '10%'],
          y: ['-5%', '15%', '-15%', '10%', '-5%'],
          scale: [0.8, 1.4, 0.9, 1.1, 0.8],
          borderRadius: [
            '70% 30% 50% 50%',
            '30% 70% 50% 50%',
            '50% 50% 70% 30%',
            '50% 50% 30% 70%',
            '70% 30% 50% 50%',
          ],
        }}
        transition={{
          duration: 22,
          repeat: Infinity,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
        }}
        className="absolute bottom-20 left-20 w-60 h-60 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, #ec4899 0%, #f472b6 40%, transparent 70%)',
          opacity: 0.05,
        }}
      />

      <div className="relative px-6 py-10 md:py-14 lg:py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 text-sm backdrop-blur-sm"
          style={{
            backgroundColor: 'rgba(var(--bg-rgb), 0.5)',
            border: '1px solid rgba(var(--text-rgb), 0.06)',
          }}
        >
          <FiBookOpen className="w-4 h-4" style={{ color: '#3b82f6' }} />
          <span style={{ color: 'var(--muted)' }}>Meet the Designer</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-4xl md:text-5xl lg:text-6xl font-bold mb-3 tracking-tight leading-[1.1]"
          style={{ color: 'var(--text)' }}
        >
          Creating meaningful
          <span className="block" style={{ color: 'var(--muted)' }}>
            visual stories
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-sm md:text-base max-w-2xl mx-auto leading-relaxed"
          style={{ color: 'var(--muted)', opacity: 0.8 }}
        >
          I'm WD Hajbul, a passionate graphic designer with 6+ years of
          experience in creating impactful brand experiences that resonate with
          audiences worldwide.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-6 flex items-center justify-center gap-3"
        >
          <span
            className="w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
          <span
            className="text-xs tracking-widest uppercase"
            style={{ color: 'var(--muted)', opacity: 0.3 }}
          >
            About
          </span>
          <span
            className="w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
        </motion.div>
      </div>
    </div>
  );
};

// =============================================
// JOURNEY POPUP - Advanced Glassmorphism with Authentic Stories
// =============================================
const JourneyPopup = ({ experience, onClose, position }) => {
  if (!experience) return null;

  const getStoryContent = role => {
    const stories = {
      'Product Visual Designer': {
        title: 'The Product Visual Journey',
        story: `This phase marked a transformative period in my career where I discovered the power of visual storytelling in commerce. I realized that product design isn't just about making things look good—it's about creating an emotional connection between the product and the customer. I dove deep into understanding how lighting, composition, and context can completely change how a product is perceived.

        Through countless projects, I learned that the best product visuals tell a story without words. They show the product in action, demonstrate its value, and make the customer imagine owning it. I spent months studying successful brands and understanding what makes their product photography and visualization so effective. This wasn't just about learning technical skills—it was about understanding consumer psychology and behavior.

        The most valuable lesson I learned is that every product has a unique personality, and it's my job to bring that personality to life through visuals. Whether it's a luxury item that needs to feel premium and exclusive, or a practical product that needs to feel reliable and trustworthy, the visual approach must be tailored to match.`,
        skills: [
          'Product Photography',
          '3D Visualization',
          'E-commerce Design',
          'Marketing Assets',
          'Consumer Psychology',
        ],
        icon: FiPackage,
        keyTakeaway:
          'Visual storytelling is the bridge between product and customer. When done right, it creates desire, trust, and action.',
        personalNote:
          "This is where I discovered my passion for creating visuals that not only look stunning but also drive real business results. It's incredibly satisfying to see how a well-crafted product visual can increase sales and build brand loyalty.",
      },
      'Brand Identity Designer': {
        title: 'The Brand Identity Chapter',
        story: `This period was a deep dive into the soul of brands. I discovered that a brand is far more than a logo—it's a living, breathing entity that communicates through every visual element. I learned to think holistically about brand identity, understanding how colors, typography, imagery, and even the spacing between elements contribute to a brand's personality.

        Working with diverse clients taught me that every brand has a unique story to tell. Some needed to feel modern and innovative, others wanted to convey tradition and trustworthiness, while others aimed to disrupt their industry with bold, unconventional visuals. I had to learn how to listen—really listen—to what clients wanted and translate their vision into a visual language that resonates with their audience.

        The most important lesson I learned is that a strong brand identity creates consistency across all touchpoints. When a customer sees a brand, they should immediately recognize it, feel something, and remember it. This consistency builds trust and loyalty over time. I also learned the importance of scalability—creating visual systems that work on a business card and a billboard, on social media and in physical spaces.`,
        skills: [
          'Brand Strategy',
          'Visual Systems',
          'Logo Design',
          'Brand Guidelines',
          'Color Theory',
          'Typography',
        ],
        icon: FiPenTool,
        keyTakeaway:
          "A brand is not just a logo—it's a complete visual language that tells a story and creates emotional connections.",
        personalNote:
          "Every brand I work on teaches me something new about human psychology and visual communication. I love the challenge of distilling a brand's essence into a simple, memorable visual identity.",
      },
      'Graphic Design Foundation': {
        title: 'The Foundation Years',
        story: `This was where my journey truly began. I remember the excitement of creating my first design, the frustration when things didn't look right, and the joy when I finally understood a concept that had been confusing me. These were the years where I built the foundation that everything else is built upon.

        I immersed myself in the fundamentals—typography, color theory, composition, and layout. I studied the work of design masters and analyzed why their work was so effective. I practiced relentlessly, sometimes spending hours on a single design just to get the spacing perfect. I learned that great design is often invisible—it works so well that people don't even notice it, they just feel that something is right.

        These years taught me the importance of discipline and patience. Design isn't about inspiration striking like lightning—it's about showing up every day, putting in the work, and continuously improving. I also learned that the best designers are the ones who never stop learning. The design world is always evolving, and staying curious is the key to staying relevant.`,
        skills: [
          'Typography',
          'Color Theory',
          'Layout Design',
          'Composition',
          'Design Principles',
          'Visual Communication',
        ],
        icon: FiGrid,
        keyTakeaway:
          'Strong foundations enable creative freedom and innovation. Master the rules before you break them.',
        personalNote: `These foundational years shaped my entire approach to design. I still use the principles I learned during this time in every project I work on, whether it's a simple logo or a complex brand identity.`,
      },
    };
    return stories[role] || stories['Graphic Design Foundation'];
  };

  const story = getStoryContent(experience.role);

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        style={{
          backgroundColor: 'rgba(0,0,0,0.4)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 30 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 30 }}
          transition={{ type: 'spring', stiffness: 350, damping: 28 }}
          className="relative max-w-3xl w-full rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto"
          style={{
            backgroundColor: 'var(--bg)',
            border: '1px solid var(--border)',
            boxShadow: '0 32px 80px rgba(0,0,0,0.12)',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Glassy shimmer effect */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent" />
            <div className="absolute -inset-full w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -translate-x-full animate-[shimmer_8s_infinite]" />
          </div>

          {/* Animated gradient orbs */}
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute -top-20 -right-20 w-48 h-48 rounded-full blur-3xl"
            style={{
              background:
                'radial-gradient(circle, var(--text) 0%, transparent 70%)',
              opacity: 0.02,
            }}
          />
          <motion.div
            animate={{
              scale: [1.2, 1, 1.2],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute -bottom-20 -left-20 w-48 h-48 rounded-full blur-3xl"
            style={{
              background:
                'radial-gradient(circle, var(--text) 0%, transparent 70%)',
              opacity: 0.02,
            }}
          />

          {/* Header */}
          <div
            className="relative z-10 p-6 pb-4 border-b"
            style={{ borderColor: 'var(--border)' }}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: 'var(--border)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <story.icon
                    className="w-7 h-7"
                    style={{ color: 'var(--text)' }}
                  />
                </div>
                <div>
                  <h3
                    className="text-xl font-bold"
                    style={{ color: 'var(--text)' }}
                  >
                    {story.title}
                  </h3>
                  <div
                    className="flex items-center gap-2 text-sm"
                    style={{ color: 'var(--muted)' }}
                  >
                    <FiCalendar className="w-3.5 h-3.5" />
                    <span>{experience.year}</span>
                    <span
                      className="w-px h-3"
                      style={{ backgroundColor: 'var(--border)' }}
                    />
                    <FiBriefcase className="w-3.5 h-3.5" />
                    <span>{experience.role}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2.5 rounded-xl transition-all duration-300 hover:scale-110 flex-shrink-0"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                }}
                whileHover={{
                  backgroundColor: 'var(--text)',
                  color: 'var(--bg)',
                }}
                whileTap={{ scale: 0.9 }}
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="relative z-10 p-6 space-y-5">
            {/* Story text */}
            <div
              className="p-4 rounded-xl space-y-3"
              style={{
                backgroundColor: 'var(--border)',
                border: '1px solid var(--border)',
              }}
            >
              {story.story.split('\n\n').map((paragraph, idx) => (
                <p
                  key={idx}
                  className={`text-sm leading-relaxed ${idx > 0 ? 'mt-3' : ''}`}
                  style={{ color: 'var(--text)' }}
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Personal Note */}
            <div
              className="p-4 rounded-xl"
              style={{
                backgroundColor: 'rgba(var(--text-rgb), 0.04)',
                border: '1px solid var(--border)',
              }}
            >
              <div className="flex items-start gap-2.5">
                <div
                  className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{
                    backgroundColor: 'var(--border)',
                  }}
                >
                  <FiHeart
                    className="w-3.5 h-3.5"
                    style={{ color: 'var(--text)' }}
                  />
                </div>
                <div>
                  <p
                    className="text-xs font-medium"
                    style={{ color: 'var(--text)' }}
                  >
                    Personal Note
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                    {story.personalNote}
                  </p>
                </div>
              </div>
            </div>

            {/* Skills learned */}
            <div>
              <h4
                className="text-xs font-semibold uppercase tracking-wider mb-3"
                style={{ color: 'var(--muted)' }}
              >
                Skills Developed
              </h4>
              <div className="flex flex-wrap gap-2">
                {story.skills.map((skill, idx) => (
                  <motion.span
                    key={idx}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    className="px-3.5 py-1.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    {skill}
                  </motion.span>
                ))}
              </div>
            </div>

            {/* Key takeaway */}
            <div
              className="p-3.5 rounded-xl"
              style={{
                backgroundColor: 'var(--border)',
                border: '1px solid var(--border)',
              }}
            >
              <div className="flex items-start gap-2.5">
                <div
                  className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: 'var(--bg)',
                  }}
                >
                  <FiBookOpen
                    className="w-3.5 h-3.5"
                    style={{ color: 'var(--text)' }}
                  />
                </div>
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  <span
                    className="font-medium"
                    style={{ color: 'var(--text)' }}
                  >
                    Key takeaway:
                  </span>{' '}
                  {story.keyTakeaway}
                </p>
              </div>
            </div>

            {/* Website Note */}
            <div
              className="p-4 rounded-xl"
              style={{
                backgroundColor: 'rgba(var(--text-rgb), 0.02)',
                border: '1px solid var(--border)',
              }}
            >
              <div className="flex items-start gap-2.5">
                <div
                  className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{
                    backgroundColor: 'var(--border)',
                  }}
                >
                  <FiGlobe
                    className="w-3.5 h-3.5"
                    style={{ color: 'var(--text)' }}
                  />
                </div>
                <div>
                  <p
                    className="text-xs font-medium"
                    style={{ color: 'var(--text)' }}
                  >
                    About This Website
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                    I designed and built this entire website myself—from the
                    UI/UX design to the frontend development. This portfolio is
                    not just a showcase of my design work; it's a testament to
                    my commitment to learning and growth. Every line of code,
                    every animation, and every pixel reflects my journey from a
                    curious beginner to a confident designer and developer. This
                    is my digital home where I share my work, my process, and my
                    passion for creating meaningful visual experiences.
                  </p>
                </div>
              </div>
            </div>

            {/* Close button */}
            <motion.button
              onClick={onClose}
              className="w-full py-3 rounded-xl text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 group"
              style={{
                backgroundColor: 'var(--text)',
                color: 'var(--bg)',
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>Continue Exploring</span>
              <FiArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
};

// =============================================
// JOURNEY TIMELINE - Main Component
// =============================================
const JourneyTimeline = () => {
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });

  const experiences = [
    {
      year: '2024 - Present',
      role: 'Product Visual Designer',
      company: 'Current Focus',
      description:
        'Specializing in product visual design for marketing and e-commerce. Creating compelling visual narratives that drive engagement and sales.',
      icon: FiPackage,
    },
    {
      year: '2022 - 2024',
      role: 'Brand Identity Designer',
      company: 'Professional Growth',
      description:
        'Developed comprehensive brand identities for diverse clients. Focused on creating modern, memorable, and scalable visual systems.',
      icon: FiPenTool,
    },
    {
      year: '2020 - 2022',
      role: 'Graphic Design Foundation',
      company: 'Skill Development',
      description:
        'Built a strong foundation in graphic design principles. Mastered core concepts including typography, color theory, and layout design.',
      icon: FiGrid,
    },
  ];

  const handleDotClick = (e, exp) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPopupPosition({
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
    setSelectedExperience(exp);
  };

  return (
    <>
      <div className="relative">
        {/* Central connecting line */}
        <div
          className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 hidden md:block"
          style={{ backgroundColor: 'var(--border)' }}
        />

        {experiences.map((exp, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              delay: idx * 0.15,
              duration: 0.6,
              type: 'spring',
              stiffness: 100,
            }}
            viewport={{ once: true, margin: '-50px' }}
            className={`relative flex flex-col md:flex-row items-center gap-6 md:gap-12 mb-12 last:mb-0 ${
              idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
            }`}
          >
            {/* Timeline dot - Clickable with popup */}
            <div className="absolute left-1/2 top-6 -translate-x-1/2 hidden md:flex z-20">
              <motion.button
                className="w-5 h-5 rounded-full border-2 cursor-pointer group relative"
                style={{
                  borderColor: 'var(--text)',
                  backgroundColor: 'var(--bg)',
                }}
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  delay: idx * 0.3,
                }}
                onClick={e => handleDotClick(e, exp)}
                whileHover={{ scale: 1.5 }}
                whileTap={{ scale: 0.8 }}
              >
                {/* Tooltip on hover */}
                <span
                  className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{
                    backgroundColor: 'var(--text)',
                    color: 'var(--bg)',
                  }}
                >
                  Click to learn more
                </span>
                {/* Inner dot */}
                <span
                  className="absolute inset-1 rounded-full"
                  style={{ backgroundColor: 'var(--text)', opacity: 0.3 }}
                />
              </motion.button>
            </div>

            {/* Card */}
            <motion.div
              className={`w-full md:w-[calc(50%-2rem)] ${
                idx % 2 === 0 ? 'md:pr-4' : 'md:pl-4'
              }`}
              whileHover={{ scale: 1.02, y: -4 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <GlassyCard className="p-6 rounded-2xl transition-all duration-500 relative overflow-hidden">
                <motion.div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                  style={{
                    background:
                      'linear-gradient(135deg, var(--text), transparent 70%)',
                    opacity: 0.02,
                  }}
                />

                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--text)',
                      }}
                    >
                      <exp.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <div
                        className="text-sm font-medium"
                        style={{ color: 'var(--text)' }}
                      >
                        {exp.year}
                      </div>
                      <div
                        className="text-xs opacity-60"
                        style={{ color: 'var(--muted)' }}
                      >
                        {exp.company}
                      </div>
                    </div>
                  </div>

                  <h3
                    className="text-xl font-bold mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    {exp.role}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: 'var(--muted)' }}
                  >
                    {exp.description}
                  </p>

                  <motion.div
                    className="mt-4 h-0.5 rounded-full"
                    style={{ backgroundColor: 'var(--text)', opacity: 0.3 }}
                    initial={{ width: 0 }}
                    whileInView={{ width: '40%' }}
                    transition={{ duration: 0.8, delay: idx * 0.1 + 0.3 }}
                    viewport={{ once: true }}
                  />
                </div>
              </GlassyCard>
            </motion.div>

            <div className="hidden md:block w-[calc(50%-2rem)]" />
          </motion.div>
        ))}
      </div>

      {/* Popup Portal */}
      <JourneyPopup
        experience={selectedExperience}
        onClose={() => setSelectedExperience(null)}
        position={popupPosition}
      />
    </>
  );
};

// =============================================
// EXPERTISE SECTION - Clean with Minimal Gradients
// =============================================
const ExpertiseSection = ({ services }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
      {services.map((service, index) => {
        const isHovered = hoveredIndex === index;
        return (
          <motion.div
            key={service.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.08, duration: 0.5 }}
            viewport={{ once: true }}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            className="group"
          >
            <div
              className="p-6 rounded-2xl transition-all duration-300"
              style={{
                backgroundColor: 'var(--bg)',
                border: '1px solid var(--border)',
                boxShadow: isHovered ? '0 4px 20px rgba(0,0,0,0.04)' : 'none',
              }}
            >
              {/* Minimal gradient - extremely subtle */}
              <div
                className="absolute inset-0 rounded-2xl pointer-events-none"
                style={{
                  background:
                    'linear-gradient(135deg, var(--text), transparent 90%)',
                  opacity: isHovered ? 0.02 : 0,
                  transition: 'opacity 0.4s ease',
                }}
              />

              <div className="relative z-10">
                {/* Header: Icon + Name + Percentage */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--text)',
                      }}
                    >
                      <service.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4
                        className="font-semibold text-base"
                        style={{ color: 'var(--text)' }}
                      >
                        {service.name}
                      </h4>
                      <span
                        className="text-xs opacity-60"
                        style={{ color: 'var(--muted)' }}
                      >
                        {service.category}
                      </span>
                    </div>
                  </div>
                  <span
                    className="text-sm font-bold"
                    style={{ color: 'var(--text)' }}
                  >
                    {service.proficiency}%
                  </span>
                </div>

                {/* Description - Always visible */}
                <p
                  className="text-xs leading-relaxed mb-4"
                  style={{ color: 'var(--muted)' }}
                >
                  {service.description}
                </p>

                {/* Proficiency Bar */}
                <div
                  className="h-1.5 rounded-full overflow-hidden"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <motion.div
                    className="h-full rounded-full"
                    style={{
                      backgroundColor: 'var(--text)',
                      opacity: 0.8,
                    }}
                    initial={{ width: 0 }}
                    whileInView={{ width: `${service.proficiency}%` }}
                    transition={{ duration: 1.2, delay: 0.2 + index * 0.1 }}
                    viewport={{ once: true }}
                  />
                </div>

                {/* Demand Bar */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: 'var(--muted)' }}>Client Demand</span>
                    <span
                      className="font-medium"
                      style={{ color: 'var(--text)' }}
                    >
                      {service.demand}%
                    </span>
                  </div>
                  <div
                    className="h-1 rounded-full overflow-hidden"
                    style={{ backgroundColor: 'var(--border)' }}
                  >
                    <motion.div
                      className="h-full rounded-full"
                      style={{
                        backgroundColor: 'var(--text)',
                        opacity: 0.4,
                      }}
                      initial={{ width: 0 }}
                      whileInView={{ width: `${service.demand}%` }}
                      transition={{ duration: 1.2, delay: 0.4 + index * 0.1 }}
                      viewport={{ once: true }}
                    />
                  </div>
                </div>

                {/* Demand Badge */}
                <motion.div
                  className="mt-3 inline-block"
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div
                    className="text-[10px] px-3 py-0.5 rounded-full flex items-center gap-1.5"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                    }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50" />
                    {service.demand >= 75 ? 'High Demand' : 'Growing Demand'}
                  </div>
                </motion.div>

                {/* Bottom indicator line */}
                <motion.div
                  className="mt-4 h-px rounded-full"
                  style={{
                    backgroundColor: 'var(--border)',
                    opacity: 0.3,
                  }}
                  initial={{ width: '0%' }}
                  whileInView={{ width: '100%' }}
                  transition={{ duration: 0.8, delay: 0.2 + index * 0.1 }}
                  viewport={{ once: true }}
                />
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

// =============================================
// VALUE PROPOSITION - Glassy Cards
// =============================================
const ValueProposition = () => {
  const values = [
    {
      icon: FiCompass,
      title: 'Strategic Design',
      description:
        'Every design decision is backed by research and strategy to ensure it achieves your business goals.',
    },
    {
      icon: FiLayers,
      title: 'Holistic Approach',
      description:
        'From concept to delivery, I provide end-to-end design solutions that maintain consistency across all touchpoints.',
    },
    {
      icon: FiTarget,
      title: 'Client-Centric',
      description:
        'Your vision is my priority. I collaborate closely to ensure the final result exceeds your expectations.',
    },
    {
      icon: FiZap,
      title: 'Innovation Focused',
      description:
        'I stay ahead of design trends and technologies to deliver modern, forward-thinking solutions.',
    },
  ];

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
      {values.map((item, idx) => (
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.1, duration: 0.5 }}
          viewport={{ once: true }}
          whileHover={{ y: -8 }}
          className="group"
        >
          <GlassyCard className="p-6 text-center rounded-2xl transition-all duration-500 h-full relative overflow-hidden">
            <motion.div
              className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
              style={{
                background:
                  'radial-gradient(circle at center, var(--text), transparent 70%)',
                opacity: 0.02,
              }}
            />

            <div className="relative z-10">
              <motion.div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--text)',
                }}
                whileHover={{ rotate: 6, scale: 1.1 }}
              >
                <item.icon className="w-7 h-7" />
              </motion.div>
              <h3
                className="font-bold text-lg mb-2"
                style={{ color: 'var(--text)' }}
              >
                {item.title}
              </h3>
              <p
                className="text-sm leading-relaxed"
                style={{ color: 'var(--muted)' }}
              >
                {item.description}
              </p>
              <motion.div
                className="w-8 h-0.5 mx-auto mt-4 rounded-full"
                style={{ backgroundColor: 'var(--text)', opacity: 0.3 }}
                initial={{ width: 0 }}
                whileInView={{ width: '2rem' }}
                transition={{ duration: 0.6, delay: idx * 0.1 + 0.3 }}
                viewport={{ once: true }}
              />
            </div>
          </GlassyCard>
        </motion.div>
      ))}
    </div>
  );
};

// =============================================
// MAIN ABOUT COMPONENT
// =============================================
function About() {
  // Services data
  const services = [
    {
      name: 'Brand Identity Design',
      icon: FiPenTool,
      category: 'Branding',
      proficiency: 95,
      demand: 92,
      description:
        'Complete brand identity design and strategy for modern businesses',
    },
    {
      name: 'Product Visual Design',
      icon: FiPackage,
      category: 'Visual Design',
      proficiency: 90,
      demand: 88,
      description:
        'Product visualization for marketing and e-commerce platforms',
    },
    {
      name: 'Image Tracing & Vectorization',
      icon: FiEye,
      category: 'Illustration',
      proficiency: 88,
      demand: 85,
      description: 'Professional image tracing and high-quality vectorization',
    },
    {
      name: 'Logo Design',
      icon: FiGrid,
      category: 'Branding',
      proficiency: 92,
      demand: 95,
      description: 'Modern, memorable, and scalable logo design for brands',
    },
  ];

  return (
    <>
      {/*
        ✅ HELMET - Only basic page info
        ❌ NO OG TAGS - Server handles OG generation via API
        Social media scrapers get OG from /api/social-preview?page=about
      */}
      <Helmet>
        <title>About | WD Hajbul - Brand & Product Visual Designer</title>
        <meta
          name="description"
          content="Learn about WD Hajbul, a creative designer specializing in brand identity and product visuals."
        />
        <meta
          name="keywords"
          content="about, designer, WD Hajbul, brand designer, product designer, graphic designer, creative, portfolio"
        />
        <link rel="canonical" href="https://wdhejbul.com/about" />
        <meta name="robots" content="index, follow" />

        {/* Schema for About page */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: 'About WD Hajbul',
            description:
              'Learn about WD Hajbul, a creative designer specializing in brand identity and product visuals.',
            url: 'https://wdhejbul.com/about',
            mainEntity: {
              '@type': 'Person',
              name: 'WD Hajbul',
              jobTitle: 'Brand & Product Visual Designer',
              description:
                'Professional logo design, vector tracing, and product visuals crafted for modern brands.',
              image: 'https://wdhejbul.com/profile.jpg',
              sameAs: [
                'https://www.linkedin.com/in/hejbul-rahman-tasin-127350265',
                'https://www.behance.net/wdhejbul',
                'https://github.com/wdhejbul',
                'https://www.instagram.com/wdhejbul/',
              ],
            },
          })}
        </script>
      </Helmet>

      <div
        className="min-h-screen"
        style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
      >
        {/* Hero Section */}
        <section className="pt-32 px-4 max-w-7xl mx-auto max-sm:p-5 max-sm:pt-24">
          <AboutHero />
        </section>

        <StatsSection
          fetchProjectCount={true}
          columns={4}
          variant="default"
          showBorder={true}
        />

        <GlassyDivider />

        {/* Journey Section */}
        <section className="py-20 px-4 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-sm uppercase tracking-wider opacity-60 mb-4 block"
            >
              My Journey
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: 'var(--text)' }}
            >
              From pixels to{' '}
              <span className="italic" style={{ color: 'var(--muted)' }}>
                purposeful design
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="max-w-2xl mx-auto"
              style={{ color: 'var(--muted)' }}
            >
              A journey of growth, learning, and mastery in the world of design
            </motion.p>
          </div>

          <JourneyTimeline />
        </section>

        <GlassyDivider />

        {/* Expertise Section */}
        <section
          className="py-20 px-4"
          style={{ backgroundColor: 'var(--border)' }}
        >
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-sm uppercase tracking-wider opacity-60 mb-4 block"
              >
                Expertise
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ color: 'var(--text)' }}
              >
                What I{' '}
                <span className="italic" style={{ color: 'var(--muted)' }}>
                  bring to the table
                </span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="max-w-2xl mx-auto"
                style={{ color: 'var(--muted)' }}
              >
                Years of experience across various design disciplines
              </motion.p>
            </div>

            <ExpertiseSection services={services} />

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              viewport={{ once: true }}
              className="flex flex-wrap gap-4 justify-center mt-12"
            >
              <NavLink
                to="/services"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm transition-all hover:gap-3"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                View All Services <FiArrowRight className="w-4 h-4" />
              </NavLink>
              <NavLink
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm transition-all hover:gap-3"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                Get In Touch <FiMessageCircle className="w-4 h-4" />
              </NavLink>
            </motion.div>
          </div>
        </section>

        <GlassyDivider />

        {/* Value Proposition Section */}
        <section className="py-20 px-4 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="text-sm uppercase tracking-wider opacity-60 mb-4 block"
            >
              Why Work With Me
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: 'var(--text)' }}
            >
              My{' '}
              <span className="italic" style={{ color: 'var(--muted)' }}>
                value proposition
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="max-w-2xl mx-auto"
              style={{ color: 'var(--muted)' }}
            >
              What sets me apart and why clients choose to work with me
            </motion.p>
          </div>

          <ValueProposition />
        </section>

        <GlassyDivider />

        {/* Philosophy Section */}
        <section className="py-20 px-4 max-w-4xl mx-auto text-center">
          <GlassyCard className="p-12 rounded-3xl">
            <blockquote
              className="text-2xl md:text-3xl font-light italic leading-relaxed"
              style={{ color: 'var(--text)' }}
            >
              "Every brand has a story waiting to be told. My mission is to
              transform ideas into visual narratives that connect, inspire, and
              leave a lasting impression."
            </blockquote>
            <div
              className="mt-8 flex items-center justify-center gap-2 text-sm opacity-60"
              style={{ color: 'var(--muted)' }}
            >
              <FiHeart className="w-4 h-4" />
              <span>Design Philosophy</span>
            </div>
          </GlassyCard>
        </section>

        <GlassyDivider />

        {/* Reviews Section */}
        <ReviewsSection />

        <GlassyDivider />

        {/* Fiverr Card */}
        <section className="py-12 px-4 max-w-7xl mx-auto">
          <FiverrCard variant="standard" />
        </section>

        <GlassyDivider />

        {/* CTA Section - Fixed Button Flickering */}
        <section className="py-20 px-4 max-w-4xl mx-auto">
          <GlassyCard className="relative rounded-3xl p-12 text-center overflow-hidden">
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl"
              style={{
                background:
                  'radial-gradient(circle, var(--text) 0%, transparent 70%)',
                opacity: 0.02,
              }}
            />

            <div className="relative z-10">
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <FiZap
                  className="w-12 h-12 mx-auto mb-4"
                  style={{ color: 'var(--text)' }}
                />
              </motion.div>
              <h2
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{ color: 'var(--text)' }}
              >
                Let's Create Together
              </h2>
              <p
                className="mb-8 max-w-md mx-auto"
                style={{ color: 'var(--muted)' }}
              >
                Have a project in mind? Let's collaborate and bring your vision
                to life.
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                <motion.a
                  href="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300"
                  style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Start a Project <FiArrowRight className="w-4 h-4" />
                </motion.a>

                <motion.div
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <NavLink
                    to="/portfolio"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300"
                    style={{
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    View Portfolio
                  </NavLink>
                </motion.div>
              </div>

              {/* Social Links */}
              <div
                className="flex justify-center gap-4 mt-8 pt-6"
                style={{ borderTop: '1px solid var(--border)' }}
              >
                {[
                  {
                    icon: FiGithub,
                    label: 'GitHub',
                    href: 'https://github.com/wdhejbul',
                  },
                  {
                    icon: FiLinkedin,
                    label: 'LinkedIn',
                    href: 'https://www.linkedin.com/in/hejbul-rahman-tasin-127350265',
                  },
                  {
                    icon: FiDribbble,
                    label: 'Behance',
                    href: 'https://www.behance.net/wdhejbul',
                  },
                  {
                    icon: FiInstagram,
                    label: 'Instagram',
                    href: 'https://www.instagram.com/wdhejbul/',
                  },
                ].map((social, idx) => (
                  <motion.a
                    key={idx}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-full transition-all duration-300 hover:scale-110 group"
                    style={{ color: 'var(--muted)' }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <social.icon className="w-5 h-5 transition-colors duration-300 group-hover:text-[var(--text)]" />
                  </motion.a>
                ))}
              </div>
            </div>
          </GlassyCard>
        </section>

        <style jsx>{`
          @keyframes shimmer {
            0% {
              transform: translateX(-100%) rotate(12deg);
            }
            100% {
              transform: translateX(200%) rotate(12deg);
            }
          }
        `}</style>
      </div>
    </>
  );
}

export default About;
